import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { BadgeCheck, Sparkles, Clock, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Page furniture for the programmatic location pages. Mirrors the /centres
 * directory header so a visitor landing from search sees the same product.
 */

export function SeoPage({ children }: { children: ReactNode }) {
  return <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">{children}</div>;
}

export interface SeoHeroProps {
  title: string;
  lede: string;
  /** Location-specific tags, e.g. what people here usually come in for. */
  tags?: string[];
  tagsLabel?: string;
  primaryCta?: { label: string; to: string };
  secondaryCta?: { label: string; to: string };
}

/** The three promises carried across the whole site. */
const TRUST = [
  { icon: BadgeCheck, label: 'Verified clinics' },
  { icon: Sparkles, label: 'Personalized therapy' },
  { icon: Clock, label: 'Book in minutes' },
];

export function SeoHero({
  title,
  lede,
  tags,
  tagsLabel,
  primaryCta,
  secondaryCta,
}: SeoHeroProps) {
  return (
    <header className="mb-10">
      <h1 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">{title}</h1>
      <p className="mt-2 max-w-2xl leading-relaxed text-muted-foreground">{lede}</p>

      <div className="mt-4 flex flex-wrap gap-2">
        {TRUST.map(({ icon: Icon, label }) => (
          <span
            key={label}
            className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary"
          >
            <Icon className="h-3.5 w-3.5" /> {label}
          </span>
        ))}
      </div>

      {tags && tags.length > 0 && (
        <div className="mt-5">
          {tagsLabel && <p className="text-sm text-muted-foreground">{tagsLabel}</p>}
          <div className="mt-2 flex flex-wrap gap-1.5">
            {tags.map((t) => (
              <span
                key={t}
                className="rounded-full border bg-card px-2.5 py-1 text-xs text-muted-foreground"
              >
                {t}
              </span>
            ))}
          </div>
        </div>
      )}

      {(primaryCta || secondaryCta) && (
        <div className="mt-6 flex flex-wrap gap-3">
          {primaryCta && (
            <Button asChild className="gap-1.5">
              <Link to={primaryCta.to}>
                {primaryCta.label} <ArrowRight className="size-4" />
              </Link>
            </Button>
          )}
          {secondaryCta && (
            <Button asChild variant="outline">
              <Link to={secondaryCta.to}>{secondaryCta.label}</Link>
            </Button>
          )}
        </div>
      )}
    </header>
  );
}

export interface SeoSectionProps {
  title: string;
  description?: string;
  children: ReactNode;
}

export function SeoSection({ title, description, children }: SeoSectionProps) {
  return (
    <section className="mt-12">
      <h2 className="text-xl font-bold tracking-tight text-foreground sm:text-2xl">{title}</h2>
      {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
      <div className="mt-4">{children}</div>
    </section>
  );
}

/** Question/answer block. Mirrors the FAQ schema emitted alongside it. */
export interface SeoFaqItem {
  question: string;
  answer: string;
  /** Authoritative outbound references shown under the answer. */
  links?: { label: string; href: string }[];
}

export function SeoFaq({ items }: { items: SeoFaqItem[] }) {
  return (
    <dl className="divide-y overflow-hidden rounded-2xl border bg-card shadow-sm">
      {items.map((faq) => (
        <div key={faq.question} className="p-5">
          <dt className="font-semibold text-foreground">{faq.question}</dt>
          <dd className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
            {faq.answer}
            {faq.links && faq.links.length > 0 && (
              <span className="mt-1.5 block text-xs">
                Source:{' '}
                {faq.links.map((l, i) => (
                  <span key={l.href}>
                    {i > 0 && ' · '}
                    <a href={l.href} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      {l.label}
                    </a>
                  </span>
                ))}
              </span>
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** Compact link tile used by the locality / service / condition grids. */
export function SeoLinkTile({
  to,
  title,
  meta,
}: {
  to: string;
  title: string;
  meta?: string;
}) {
  return (
    <Link
      to={to}
      className="group flex h-full items-start justify-between gap-3 rounded-xl border bg-card p-4 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-[var(--sc-teal-mid)] hover:shadow-[0_8px_24px_rgba(15,110,86,0.10)]"
    >
      <span className="min-w-0">
        <span className="block font-medium text-foreground">{title}</span>
        {meta && <span className="mt-0.5 block text-xs text-muted-foreground">{meta}</span>}
      </span>
      <ArrowRight className="mt-0.5 size-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
    </Link>
  );
}
